import type { OpencodeMessagePart } from "schemas/generated/opencode";

type PageRouteInfo = {
  route: string;
  toolAction: "write" | "edit";
};

const TOOL_COMPLETE_STATUSES = new Set([
  "completed",
  "success",
  "done",
  "finished",
]);

// Match paths like apps/user-app/app/route/page.tsx, app/route/page.tsx,
// src/app/route/page.tsx, or app/(pages)/route/page.tsx
const PAGE_ROUTE_REGEX =
  /(?:apps\/[\w-]+\/)?(?:src\/)?app\/(?:\([^)]+\)\/)?(.+?)\/page\.tsx$/;
const ROOT_PAGE_REGEX =
  /(?:apps\/[\w-]+\/)?(?:src\/)?app\/(?:\([^)]+\)\/)?page\.tsx$/;

function isToolComplete(status: string): boolean {
  return TOOL_COMPLETE_STATUSES.has(status.toLowerCase());
}

function getStringField({
  record,
  key,
}: {
  record: Record<string, unknown>;
  key: string;
}): string | null {
  const value = record[key];
  return typeof value === "string" ? value : null;
}

function extractPageRouteFromToolPart(
  part: OpencodeMessagePart,
): PageRouteInfo | null {
  const partData = part.partData;
  if (partData.type !== "tool") {
    return null;
  }

  const toolLower = partData.tool.toLowerCase();
  if (toolLower !== "write" && toolLower !== "edit") {
    return null;
  }

  if (!isToolComplete(partData.state.status)) {
    return null;
  }

  const input = partData.state.input;
  const filePath =
    getStringField({ record: input, key: "path" }) ??
    getStringField({ record: input, key: "filePath" }) ??
    getStringField({ record: input, key: "file_path" }) ??
    "";
  if (!filePath) {
    return null;
  }

  const toolAction = toolLower as "write" | "edit";

  if (filePath.match(ROOT_PAGE_REGEX)) {
    return { route: "/", toolAction };
  }

  const pageMatch = filePath.match(PAGE_ROUTE_REGEX);
  if (pageMatch?.[1]) {
    return { route: pageMatch[1], toolAction };
  }

  return null;
}

export { extractPageRouteFromToolPart };
export type { PageRouteInfo };
